// @ts-nocheck
/**
 * Admin Portal — API Client
 *
 * Separate API methods for the admin portal.
 * Uses the same base apiClient but hits /admin/* endpoints.
 */

import { apiClient } from './client'

const ADMIN_TOKEN_KEY = 'admin_token'

// Create a separate axios instance for admin (so interceptors don't conflict)
import axios from 'axios'

// In the private admin cluster, the entrypoint injects window.__VESTOLIO_ADMIN_API_URL__
// which points to /api/v1 (relative), so API calls are proxied through the private
// admin nginx to the internal API service — no traffic leaves the VPC.
const baseURL = (window as any).__VESTOLIO_ADMIN_API_URL__
  || import.meta.env.VITE_API_URL
  || (import.meta.env.DEV ? 'http://localhost:4000/api/v1' : '/api/v1')

export const adminClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

// Add admin token to requests
adminClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle 401 — redirect to admin login
adminClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(ADMIN_TOKEN_KEY)
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login'
      }
    }
    return Promise.reject(error)
  }
)

export const adminApi = {
  auth: {
    login: (email: string, password: string) =>
      adminClient.post('/admin/auth/login', { email, password }).then(r => r.data),
    setup2FA: () =>
      adminClient.post('/admin/auth/setup-2fa').then(r => r.data),
    verify2FA: (code: string) =>
      adminClient.post('/admin/auth/verify-2fa', { code }).then(r => r.data),
    me: () =>
      adminClient.get('/admin/auth/me').then(r => r.data),
    changePassword: (currentPassword: string, newPassword: string) =>
      adminClient.post('/admin/auth/change-password', { currentPassword, newPassword }).then(r => r.data),
    acceptInvite: (email: string, token: string, password: string) =>
      adminClient.post('/admin/auth/accept-invite', { email, token, password }).then(r => r.data),
    acceptTenantInvite: (email: string, token: string, password: string) =>
      adminClient.post('/admin/auth/accept-tenant-invite', { email, token, password }).then(r => r.data),
    requestPasswordReset: (email: string) =>
      adminClient.post('/admin/auth/request-password-reset', { email }).then(r => r.data),
    resetPassword: (email: string, code: string, newPassword: string) =>
      adminClient.post('/admin/auth/reset-password', { email, code, newPassword }).then(r => r.data),
    setToken: (token: string) => localStorage.setItem(ADMIN_TOKEN_KEY, token),
    getToken: () => localStorage.getItem(ADMIN_TOKEN_KEY),
    clearToken: () => localStorage.removeItem(ADMIN_TOKEN_KEY),
  },
  users: {
    list: () =>
      adminClient.get('/admin/users').then(r => r.data),
    create: (data: { email: string; fullName: string; role?: string }) =>
      adminClient.post('/admin/users', data).then(r => r.data),
    update: (id: string, data: { fullName?: string; role?: string; isActive?: boolean }) =>
      adminClient.patch(`/admin/users/${id}`, data).then(r => r.data),
    delete: (id: string) =>
      adminClient.delete(`/admin/users/${id}`).then(r => r.data),
    resendInvite: (id: string) =>
      adminClient.post(`/admin/users/${id}/resend-invite`).then(r => r.data),
    triggerPasswordReset: (id: string) =>
      adminClient.post(`/admin/users/${id}/reset-password`).then(r => r.data),
  },
  organizations: {
    list: () =>
      adminClient.get('/admin/organizations').then(r => r.data),
    get: (id: string) =>
      adminClient.get(`/admin/organizations/${id}`).then(r => r.data),
    create: (data: { name: string; slug: string; domain?: string; plan?: string }) =>
      adminClient.post('/admin/organizations', data).then(r => r.data),
    update: (id: string, data: any) =>
      adminClient.patch(`/admin/organizations/${id}`, data).then(r => r.data),
    delete: (id: string, hard?: boolean) =>
      adminClient.delete(`/admin/organizations/${id}${hard ? '?hard=true' : ''}`).then(r => r.data),
    reactivate: (id: string) =>
      adminClient.post(`/admin/organizations/${id}/reactivate`).then(r => r.data),
    listUsers: (id: string) =>
      adminClient.get(`/admin/organizations/${id}/users`).then(r => r.data),
    listClients: (id: string) =>
      adminClient.get(`/admin/organizations/${id}/clients`).then(r => r.data),
    updateClient: (orgId: string, clientId: string, data: any) =>
      adminClient.patch(`/admin/organizations/${orgId}/clients/${clientId}`, data).then(r => r.data),
    updateUser: (orgId: string, userId: string, data: any) =>
      adminClient.patch(`/admin/organizations/${orgId}/users/${userId}`, data).then(r => r.data),
    changeOwner: (orgId: string, userId: string) =>
      adminClient.patch(`/admin/organizations/${orgId}/change-owner`, { userId }).then(r => r.data),
    inviteUser: (orgId: string, data: { email: string; name: string; role: string; phone?: string; assignedRoleId?: string; nodeId?: string }) =>
      adminClient.post(`/admin/organizations/${orgId}/invite-user`, data).then(r => r.data),
    resendUserInvite: (orgId: string, userId: string) =>
      adminClient.post(`/admin/organizations/${orgId}/users/${userId}/resend-invite`).then(r => r.data),
    getUserPermissions: (orgId: string, userId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/users/${userId}/permissions`).then(r => r.data),
    updateUserPermissions: (orgId: string, userId: string, permissions: Record<string, Record<string, boolean>>) =>
      adminClient.put(`/admin/organizations/${orgId}/users/${userId}/permissions`, { permissions }).then(r => r.data),
    // Branding
    getBranding: (orgId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/branding`).then(r => r.data),
    updateBranding: (orgId: string, data: { company_name?: string; company_logo?: string; company_icon_logo?: string }) =>
      adminClient.put(`/admin/organizations/${orgId}/branding`, data).then(r => r.data),
    uploadBrandingFile: (orgId: string, file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return adminClient.post(`/admin/organizations/${orgId}/branding/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(r => r.data)
    },
  },
  auditLogs: {
    list: (params?: { page?: number; limit?: number; adminId?: string; resourceType?: string; dateFrom?: string; dateTo?: string }) =>
      adminClient.get('/admin/audit-logs', { params }).then(r => r.data),
  },
  orgHierarchy: {
    // Tiers
    listTiers: (orgId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/tiers`).then(r => r.data),
    createTier: (orgId: string, data: any) =>
      adminClient.post(`/admin/organizations/${orgId}/hierarchy/tiers`, data).then(r => r.data),
    bulkCreateTiers: (orgId: string, tiers: { name: string; icon?: string }[]) =>
      adminClient.post(`/admin/organizations/${orgId}/hierarchy/tiers/bulk`, { tiers }).then(r => r.data),
    updateTier: (orgId: string, tierId: string, data: any) =>
      adminClient.put(`/admin/organizations/${orgId}/hierarchy/tiers/${tierId}`, data).then(r => r.data),
    deleteTier: (orgId: string, tierId: string) =>
      adminClient.delete(`/admin/organizations/${orgId}/hierarchy/tiers/${tierId}`).then(r => r.data),
    reorderTiers: (orgId: string, tiers: { id: string; level: number }[]) =>
      adminClient.post(`/admin/organizations/${orgId}/hierarchy/tiers/reorder`, { tiers }).then(r => r.data),

    // Tree & Nodes
    getTree: (orgId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/tree`).then(r => r.data),
    listNodes: (orgId: string, params?: any) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/nodes`, { params }).then(r => r.data),
    createNode: (orgId: string, data: any) =>
      adminClient.post(`/admin/organizations/${orgId}/hierarchy/nodes`, data).then(r => r.data),
    updateNode: (orgId: string, nodeId: string, data: any) =>
      adminClient.put(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}`, data).then(r => r.data),
    deleteNode: (orgId: string, nodeId: string) =>
      adminClient.delete(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}`).then(r => r.data),
    moveNode: (orgId: string, nodeId: string, parentId: string | null) =>
      adminClient.post(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/move`, { parentId }).then(r => r.data),

    // Members
    listMembers: (orgId: string, nodeId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/members`).then(r => r.data),
    assignMembers: (orgId: string, nodeId: string, userIds: string[], role?: string, force?: boolean) =>
      adminClient.post(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/members`, { userIds, role, force }).then(r => r.data),
    removeMember: (orgId: string, nodeId: string, userId: string) =>
      adminClient.delete(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/members/${userId}`).then(r => r.data),

    // Clients
    listClients: (orgId: string, nodeId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/clients`).then(r => r.data),
    assignClients: (orgId: string, nodeId: string, clientIds: string[]) =>
      adminClient.post(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/clients`, { clientIds }).then(r => r.data),
    removeClient: (orgId: string, nodeId: string, clientId: string) =>
      adminClient.delete(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/clients/${clientId}`).then(r => r.data),

    // Org users/clients (for assignment dialogs)
    listOrgUsers: (orgId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/users`).then(r => r.data),
    listOrgClients: (orgId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/clients`).then(r => r.data),    // Node permissions overview
    getNodePermissions: (orgId: string, nodeId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/nodes/${nodeId}/permissions`).then(r => r.data),
    // User scope
    getUserScope: (orgId: string, userId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/hierarchy/user/${userId}/scope`).then(r => r.data),
  },
  // Organization Roles (custom permission roles)
  orgRoles: {
    list: (orgId: string) =>
      adminClient.get(`/admin/organizations/${orgId}/roles`).then(r => r.data),
    create: (orgId: string, data: { name: string; description?: string; permissions: Record<string, Record<string, boolean>> }) =>
      adminClient.post(`/admin/organizations/${orgId}/roles`, data).then(r => r.data),
    update: (orgId: string, roleId: string, data: { name?: string; description?: string; permissions?: Record<string, Record<string, boolean>> }) =>
      adminClient.put(`/admin/organizations/${orgId}/roles/${roleId}`, data).then(r => r.data),
    delete: (orgId: string, roleId: string) =>
      adminClient.delete(`/admin/organizations/${orgId}/roles/${roleId}`).then(r => r.data),
  },
}
